import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryKeys } from './query-client';

// orion-web's CLIENT bundle must not import common-lib, so the brief shape lives
// here as a client-local contract matching what /api/brief returns.

export interface BriefMessage {
  id: string;
  content: string;
  createdDate: string;
  // Raw metadata from the conversation-message row. The client interprets it (agentId, the
  // agentMeetingSummary wrap-up flag) exactly like the native apps parse the same rows — so a new
  // marker never needs an API change. Read it via briefMessageAgentId / isBriefSummaryTurn below.
  metadata?: Record<string, unknown> | string;
}

export interface MorningBrief {
  conversationId: string;
  // The meeting's own title (the topic the facilitator chose) — shown as the conversation name on every
  // surface. OPTIONAL: the facilitator names the meeting at wrap-up, so a brief that is still convening
  // carries no name at all — the wire OMITS the key rather than sending `""`. #7155 made in-motion
  // briefs showable and declared this `string` in the same change, then read `brief.name.trim()`, so
  // every convening brief crashed the home with a TypeError. `fetchMorningBrief` only CASTS the
  // response, so the non-optional type was never a guarantee. Consumers already render
  // `title || t('brief.title')`, so an absent name is a supported state, not an error.
  name?: string;
  // The round-table's lifecycle: created inactive, flips active at the wrap-up. `false` + not `failed`
  // => still convening ("in motion"); `true` => finished.
  active: boolean;
  // The backend could not start/advance this meeting (agent BriefService stamps `metadata.briefFailed`).
  // Terminal — never render it as an in-motion hero.
  failed: boolean;
  generatedDate: string;
  // `metadata.teaser` off the BRIEF conversation itself — the short catch-up-glance headline the
  // harness authored. Mirrors iOS `MorningStandup.swift` / Android `Standup.kt`.
  teaser?: string;
  messages: BriefMessage[];
}

export async function fetchMorningBrief(): Promise<MorningBrief | null> {
  const res = await apiFetch(`${API_BASE}/api/brief`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { brief?: MorningBrief | null };
  return data.brief ?? null;
}

// `undefined` = not loaded yet; `null` = loaded, no brief exists.
export function useMorningBrief(): MorningBrief | null | undefined {
  return useMorningBriefQuery().brief;
}

/**
 * The brief plus whether its fetch has finished. A failed fetch leaves `brief` `undefined` forever, so a
 * caller that has to decide something ("show the brief, or the card that stands in for it?") can't read
 * "still loading" off the value alone — it needs `settled`.
 */
export function useMorningBriefQuery(): { brief: MorningBrief | null | undefined; settled: boolean } {
  const { data, isFetched, refetch } = useQuery({
    queryKey: queryKeys.brief,
    queryFn: fetchMorningBrief,
  });
  useFileSystemDuplexer(() => void refetch());
  // `isFetched` rather than a `status` literal: it means "a fetch has finished, either way", which is
  // exactly the question, and unlike the status union it has not been renamed across React Query majors.
  // brief.test.ts pins that a REJECTED query reports it, since the caller's fallback depends on that.
  return { brief: data, settled: isFetched };
}

// `metadata` may be a JSON object OR a JSON-encoded string (the wire is a stringified map), so
// normalize before reading keys. Mirrors iOS `SymphonyAPI` / Android `parseAgentMeetingSummary`.
function readMeta(metadata: BriefMessage['metadata']): Record<string, unknown> | undefined {
  if (metadata && typeof metadata === 'object') return metadata;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

// The brief orchestrator stamps `agentMeetingSummary` as a JSON bool OR a stringified `"true"`
// (map<string,string> metadata). Accept both — a strict `=== true` silently misses the string form.
function isTruthyFlag(value: unknown): boolean {
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

// A turn's authoring sub-agent (`metadata.agentId`), or undefined for the Maestro's own turn.
export function briefMessageAgentId(m: BriefMessage): string | undefined {
  const v = readMeta(m.metadata)?.agentId;
  return typeof v === 'string' && v ? v : undefined;
}

export function briefMessageAgentName(m: BriefMessage): string | undefined {
  const name = readMeta(m.metadata)?.agentName;
  return typeof name === 'string' && name.trim() ? name : undefined;
}

// Whether this turn is the round-table's wrap-up summary (`metadata.agentMeetingSummary`).
export function isBriefSummaryTurn(m: BriefMessage): boolean {
  return isTruthyFlag(readMeta(m.metadata)?.agentMeetingSummary);
}

// The round-table's wrap-up turn — the headline line for the glance. Prefers the turn the harness
// flagged with `agentMeetingSummary`; falls back to the last Maestro turn (no agentId), then to the
// last turn overall, so the brief always has something to show.
export function maestroSummary(brief: MorningBrief): BriefMessage | undefined {
  const flagged = brief.messages.find(isBriefSummaryTurn);
  if (flagged) return flagged;
  const maestro = brief.messages.filter((m) => !briefMessageAgentId(m));
  return maestro[maestro.length - 1] ?? brief.messages[brief.messages.length - 1];
}

// How long after creation an inactive-but-not-failed brief still counts as "in motion". A round-table
// normally wraps in well under this; past it, an inactive brief with no failed flag is a stale/legacy
// strand, not a live meeting, so we stop presenting it as convening. Mirrors the native window.
const BRIEF_IN_MOTION_WINDOW_MS = 6 * 60 * 60 * 1000;

// Whether the brief is still convening — created, not yet wrapped up (inactive), not failed, and recent.
// This is the "in motion" state the home shows instead of falling back to a prior day's brief.
export function briefIsInMotion(brief: MorningBrief): boolean {
  if (brief.active || brief.failed) return false;
  const created = new Date(brief.generatedDate).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created < BRIEF_IN_MOTION_WINDOW_MS;
}

// Whether the brief slot should render this brief at all: a finished brief with content, or one that is
// still in motion. A failed brief, or a stale inactive one, is not showable (the slot falls back to its
// other states rather than to yesterday's brief).
export function briefIsShowable(brief: MorningBrief | null | undefined): boolean {
  if (!brief || brief.failed) return false;
  if (briefIsInMotion(brief)) return true;
  return brief.active && brief.messages.length > 0;
}

// Distinct agent ids that spoke, in first-spoke order — the avatar row for the glance.
export function briefAgentIds(brief: MorningBrief): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const m of brief.messages) {
    const id = briefMessageAgentId(m);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}
